/**
 * Money, in integer minor units. No float ever touches an amount.
 *
 * These live in contracts rather than in the API's commission domain because BOTH sides
 * add money up: the domain computes accruals and the statements view checks that a
 * breakdown adds to its total. When they were API-only, the portal wrote its own
 * `Math.round(Number(amount) * 100)` — the float multiply this file exists to avoid,
 * returning NaN where these throw — so the figure a partner was shown to verify the
 * arithmetic was computed by a different rule from the arithmetic itself.
 */

/** "249.00" → 24900. Rejects anything that is not a plain 2-decimal amount. */
export function toMinor(amount: string): number {
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(amount.trim());
  if (!match) {
    throw new Error(`Not a money amount: "${amount}". Expected e.g. "249.00" or "-12.34".`);
  }

  const [, sign, whole, fraction = '0'] = match;
  const minor = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return sign === '-' ? -minor : minor;
}

/** 24900 → "249.00". */
export function fromMinor(minor: number): string {
  const negative = minor < 0;
  const absolute = Math.abs(Math.round(minor));
  const whole = Math.floor(absolute / 100);
  const fraction = String(absolute % 100).padStart(2, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/** Adds a list of 2-decimal amounts without ever leaving integer arithmetic. */
export function sumMoney(amounts: readonly string[]): string {
  return fromMinor(amounts.reduce((total, amount) => total + toMinor(amount), 0));
}
